import React, { useEffect, useState } from 'react';
import { Copy, Mail, Share2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';

type ReportShareBarProps = {
  reportId: string;
  title: string;
};

const getShareUrl = (reportId: string): string =>
  `${window.location.origin}/reports/${reportId}`;

export const ReportShareBar: React.FC<ReportShareBarProps> = ({ reportId, title }) => {
  const { toast } = useToast();
  const [supportsNativeShare, setSupportsNativeShare] = useState(false);

  useEffect(() => {
    setSupportsNativeShare(typeof navigator.share === 'function');
  }, []);

  const resolvedTitle = title.trim();
  const shareUrl = getShareUrl(reportId);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast({ title: 'Link copiado' });
    } catch {
      toast({
        title: 'Não foi possível copiar o link',
        variant: 'error',
      });
    }
  };

  const shareWhatsApp = () => {
    const text = resolvedTitle ? `${resolvedTitle} ${shareUrl}` : shareUrl;
    window.open(
      `https://wa.me/?text=${encodeURIComponent(text)}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  const shareEmail = () => {
    const subject = resolvedTitle || 'Relatório';
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(shareUrl)}`;
  };

  const nativeShare = async () => {
    if (!supportsNativeShare) {
      await copyLink();
      return;
    }

    try {
      await navigator.share({
        title: resolvedTitle || undefined,
        text: resolvedTitle || undefined,
        url: shareUrl,
      });
    } catch {
      return;
    }
  };

  return (
    <div
      className="fixed bottom-4 left-1/2 z-50 flex w-max max-w-[calc(100vw-2rem)] -translate-x-1/2 flex-nowrap items-center justify-center gap-1 rounded-full border border-black/10 bg-black px-2 py-2 shadow-lg sm:gap-2 sm:px-3"
      role="group"
      aria-label="Compartilhar relatório"
      data-share-scope="report"
    >
      <Button
        type="button"
        variant="ghost"
        size="sm"
        data-share-method="whatsapp"
        className="h-8 shrink-0 rounded-full px-2.5 text-white hover:bg-white/10 hover:text-white sm:px-3"
        onClick={shareWhatsApp}
      >
        WhatsApp
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        data-share-method="email"
        className="h-8 shrink-0 rounded-full px-2.5 text-white hover:bg-white/10 hover:text-white sm:px-3"
        onClick={shareEmail}
      >
        <Mail className="mr-1 h-4 w-4 shrink-0" />
        E-mail
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        data-share-method="copy_link"
        className="h-8 shrink-0 rounded-full px-2.5 text-white hover:bg-white/10 hover:text-white sm:px-3"
        onClick={copyLink}
      >
        <Copy className="mr-1 h-4 w-4 shrink-0" />
        Copiar link
      </Button>
      {supportsNativeShare ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          data-share-method="native_share"
          className="h-8 shrink-0 rounded-full px-2.5 text-white hover:bg-white/10 hover:text-white sm:px-3"
          onClick={nativeShare}
        >
          <Share2 className="mr-1 h-4 w-4 shrink-0" />
          Compartilhar
        </Button>
      ) : null}
    </div>
  );
};
